import { forwardRef } from 'react';
import { Pause, Play } from 'lucide-react';
import { cn } from '@ai-tutor/ui';

/** `3:07` - minutes and seconds. */
export function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export interface PodcastPillProps {
  isPlaying: boolean;
  sectionIndex: number;
  sectionTitle: string;
  currentTime: number;
  duration: number;
  expanded: boolean;
  onToggle: () => void;
  onOpen: () => void;
}

/**
 * The playing podcast in the Navbar, beside the focus timer:
 * `[❚❚] S2 · Light reactions  3:12`. Two controls in one pill: the icon
 * plays or pauses where you are, the rest opens `PodcastPanel`.
 *
 * Neutral, where the running focus pill is filled: the two never read as the
 * same control. A thin line along the bottom is how far it has played. The
 * section title only shows from `lg` (as the focus pill's intention does),
 * so on a phone the two pills fit beside each other.
 */
export const PodcastPill = forwardRef<HTMLButtonElement, PodcastPillProps>(
  (
    {
      isPlaying,
      sectionIndex,
      sectionTitle,
      currentTime,
      duration,
      expanded,
      onToggle,
      onOpen,
    },
    ref
  ) => {
    const left = duration > 0 ? duration - currentTime : 0;
    const played = duration > 0 ? Math.min(1, currentTime / duration) : 0;
    return (
      <div className='relative inline-flex h-9 items-center rounded-full border border-border bg-card text-sm text-foreground'>
        <button
          type='button'
          onClick={onToggle}
          aria-label={isPlaying ? 'Pause podcast' : 'Play podcast'}
          className='tap-target flex h-9 w-11 items-center justify-center rounded-full transition-colors duration-fast hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
        >
          {isPlaying ? (
            <Pause className='h-3.5 w-3.5' aria-hidden='true' />
          ) : (
            <Play className='h-3.5 w-3.5' aria-hidden='true' />
          )}
        </button>
        <button
          ref={ref}
          type='button'
          onClick={onOpen}
          aria-expanded={expanded}
          aria-haspopup='dialog'
          aria-label={`Podcast, section ${sectionIndex}: ${sectionTitle}`}
          className='tap-target flex h-9 items-center gap-2 rounded-full pl-1 pr-3 transition-colors duration-fast hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
        >
          <span
            className='hidden max-w-[10rem] truncate lg:inline'
            aria-hidden='true'
          >
            <span className='font-mono text-xs text-muted-foreground'>
              S{sectionIndex} ·{' '}
            </span>
            {sectionTitle}
          </span>
          <span
            className='font-mono text-xs tabular-nums text-muted-foreground'
            aria-hidden='true'
          >
            {formatClock(left)}
          </span>
        </button>
        <span
          className='pointer-events-none absolute inset-x-3 bottom-0 h-0.5 rounded-full bg-muted'
          aria-hidden='true'
        >
          <span
            className={cn(
              'block h-full rounded-full bg-primary',
              'motion-safe:transition-[width] motion-safe:duration-300'
            )}
            style={{ width: `${played * 100}%` }}
          />
        </span>
      </div>
    );
  }
);
PodcastPill.displayName = 'PodcastPill';
